import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import axios from "axios";
import { getCoverUrl } from "@/lib/mediaUrls";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  return (
    <nav className="nav-bar" data-testid="main-navigation">
      <div className="container-custom flex items-center justify-between py-4">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img src="/rgm-logo.png" alt="RGM Studio" className="h-10 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/featured">
            <Button variant="ghost" className="text-white" data-testid="nav-featured-btn">
              Featured Artists
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="artist-portal-btn" data-testid="nav-login-btn">
              Artist Portal
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

const FeaturedArtists = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingIndex, setPlayingIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedArtists();
  }, []);

  const fetchFeaturedArtists = async () => {
    try {
      const response = await axios.get(`${API}/submissions/featured`);
      setArtists(response.data);
    } catch (error) {
      console.error("Failed to fetch featured artists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMusic = () => {
    navigate("/login");
  };

  const handleListen = (index) => {
    setPlayingIndex(prev => (prev === index ? null : index));
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
        <Navigation />
        <div className="container-custom py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading featured artists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      <Navigation />

      {/* Hero Section */}
      <section className="featured-hero" data-testid="featured-hero">
        <div className="container-custom">
          <div className="text-center py-16">
            <h1 className="featured-title" data-testid="featured-title">
              This Week's Featured Artists
            </h1>
            <p className="featured-subtitle" data-testid="featured-subtitle">
              Discover the top 3 artists selected by RGM Studio™
            </p>
          </div>
        </div>
      </section>

      {/* Featured Artists Grid */}
      <section className="section-padding" data-testid="artists-section">
        <div className="container-custom">
          {artists.length === 0 ? (
            <div className="text-center py-12">
              <Card 
                className="max-w-2xl mx-auto p-8"
                style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}
              >
                <h3 className="text-2xl font-bold text-white mb-4" data-testid="no-artists-title">
                  No Featured Artists This Week
                </h3>
                <p className="text-gray-400 mb-6">
                  Be the first to get featured! Submit your music today and get a chance to be showcased.
                </p>
                <Button
                  onClick={handleSubmitMusic}
                  className="submit-music-btn"
                  data-testid="no-artists-cta"
                >
                  Submit Your Music
                </Button>
              </Card>
            </div>
          ) : (
            <div className="featured-grid">
              {artists.map((artist, index) => (
                <Card
                  key={artist.submission_id}
                  className="artist-card"
                  data-testid={`artist-card-${index}`}
                  style={{
                    background: 'rgba(42, 26, 58, 0.5)',
                    border: '1px solid rgba(138, 43, 226, 0.3)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Cover Art */}
                  <div className="artist-cover" style={{ position: 'relative', paddingTop: '100%', overflow: 'hidden' }}>
                    <img
                      src={getCoverUrl(artist)}
                      alt={artist.artist_name}
                      className="artist-cover-image"
                      data-testid={`artist-cover-${index}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {index === 0 && (
                      <div
                        className="featured-badge"
                        style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                          color: '#000',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        #1 This Week
                      </div>
                    )}
                  </div>

                  {/* Artist Info */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2" data-testid={`artist-name-${index}`}>
                      {artist.artist_name}
                    </h3>
                    
                    <div className="flex items-center gap-3 mb-4 text-gray-400 text-sm">
                      <span data-testid={`artist-location-${index}`}>
                        {artist.city}, {artist.country}
                      </span>
                      <span>•</span>
                      <span className="text-purple-400 font-semibold" data-testid={`artist-genre-${index}`}>
                        {artist.genre}
                      </span>
                    </div>

                    <p className="text-gray-300 mb-6 line-clamp-3" data-testid={`artist-bio-${index}`}>
                      {artist.bio}
                    </p>

                    {/* Social Links */}
                    {(artist.instagram || artist.youtube || artist.tiktok || artist.spotify) && (
                      <div className="flex gap-2 mb-4">
                        {artist.instagram && (
                          <a
                            href={artist.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            data-testid={`artist-instagram-${index}`}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                          </a>
                        )}
                        {artist.youtube && (
                          <a
                            href={artist.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            data-testid={`artist-youtube-${index}`}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                          </a>
                        )}
                        {artist.tiktok && (
                          <a
                            href={artist.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            data-testid={`artist-tiktok-${index}`}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                          </a>
                        )}
                        {artist.spotify && (
                          <a
                            href={artist.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            data-testid={`artist-spotify-${index}`}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    )}

                    {/* Listen Button / Inline Player */}
                    {playingIndex === index ? (
                      <div className="mt-2">
                        <audio
                          controls
                          autoPlay
                          src={`${API}/public/music/${artist.music_file}`}
                          className="w-full"
                          data-testid={`inline-audio-${index}`}
                        />
                        <Button
                          onClick={() => handleListen(index)}
                          variant="outline"
                          className="w-full mt-2 text-sm"
                          data-testid={`close-player-${index}`}
                        >
                          Close Player
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleListen(index)}
                        className="w-full submit-music-btn"
                        data-testid={`listen-button-${index}`}
                      >
                        ▶ Listen Now
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <Card
              className="max-w-3xl mx-auto p-8"
              style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}
            >
              <h3 className="text-3xl font-bold text-white mb-4" data-testid="cta-title">
                Want to Be Featured?
              </h3>
              <p className="text-gray-300 text-lg mb-6">
                Submit your music today for a chance to be selected as one of next week's top 3 artists.
                Submissions are FREE during our launch period!
              </p>
              <Button
                onClick={handleSubmitMusic}
                className="submit-music-btn text-lg px-8 py-6"
                data-testid="submit-cta-button"
              >
                Submit Your Music Now
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-custom">
          <p className="footer-text">© 2026 Real Global Music. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FeaturedArtists;
